import { type JSX, useCallback, useEffect, useState } from 'react';

import {
  antworten,
  benachrichtigungen as holeBenachrichtigungen,
  benachrichtigungGelesen,
  einsaetze as holeEinsaetze,
  ich as holeIch,
  rueckmelden,
  tagesbild as holeTagesbild,
  teilnehmer as holeTeilnehmer,
  RUECKMELDUNGSARTEN,
  RUECKMELDUNGSNAME,
} from '../api/trainerbereich';
import type {
  Einsatz,
  Eigene,
  Gruppenteilnehmer,
  Rueckmeldungsart,
  Tagesbild,
  Trainernachricht,
} from '../api/trainerbereich';
import { ApiFehler } from '../api/fehler';
import { Meldung } from '../komponenten/Meldung';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import { Sinnbild } from '../komponenten/Sinnbild';

/**
 * DER DIENSTLEISTERKALENDER (RES-27, RES-29 bis RES-32).
 *
 * ============================================================================
 * DIE EINZIGE MASKE DIESES SYSTEMS, DIE EIN EXTERNER SIEHT
 * ============================================================================
 *
 * RES-30 schreibt ihr vier Dinge vor, und alle vier sind hier Bauvorschrift:
 *
 * 1. AB 360 PIXEL OHNE ZOOMEN. Eine Spalte, keine Tabelle, Schriftgrößen in
 *    `rem`. Die Einsätze stehen als Karten untereinander — eine Tabelle mit
 *    acht Spalten ist auf einem Telefon unlesbar, und waagerechtes Schieben
 *    ist genau das Zoomen, das RES-30 ausschließt.
 *
 * 2. DREI BEDIENSCHRITTE, UNTER DREISSIG SEKUNDEN. Die offenen Anfragen stehen
 *    OBEN und tragen ihre beiden Knöpfe an der Zeile: Einstieg, Knopf, fertig.
 *    Kein Aufklappen, kein Wechsel in eine Unterseite, keine Rückfrage „wirklich?"
 *    bei der Zusage — sie ist einer der drei Handgriffe, die RES-30 zählt. Die
 *    Absage öffnet die Rückfrage im Pop-up (Auftrag CRM-104), und darin steht
 *    auch das freiwillige Feld für den Grund: Einstieg, „Absagen …", „Absage
 *    senden" — dieselben drei Schritte wie vorher mit dem Feld an der Karte.
 *
 * 3. KEINE CRM-NAVIGATION. Diese Maske steht NEBEN der Arbeitsfläche und nicht
 *    darin: App.tsx schickt ein Dienstleisterkonto hierher, und der Rahmen mit
 *    Kopfleiste, Seitennavigation und Suche wird nie geladen. Das ist keine
 *    Sparsamkeit — wer hier ist, hat in keinem anderen Bereich etwas zu suchen.
 *
 * 4. NUR DIE ANGABEN NACH RES-31. Die Maske zeigt, was die Antwort enthält, und
 *    die Antwort enthält nur Tag, Zeit, Dauer, Ort, Leistung, Stand und Frist.
 *    Es gibt hier nichts zu verstecken, weil nichts Verstecktes ankommt — die
 *    Grenze steht im Server (RES-32).
 */

/** Was der Stand einer Belegung für den Angefragten bedeutet. */
const STANDNAME: Record<string, string> = {
  vorlaeufig: 'angefragt',
  zugesagt: 'zugesagt',
  bestaetigt: 'bestätigt',
};

/** Wochentag und Datum, wie ein Mensch es liest (FRS-01, Europe/Berlin). */
function alsTag(iso: string): string {
  const [jahr, monat, tag] = iso.split('-').map(Number);
  const datum = new Date(Date.UTC(jahr ?? 1970, (monat ?? 1) - 1, tag ?? 1));
  return datum.toLocaleDateString('de-DE', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * Der heutige Kalendertag in Ortszeit, als `JJJJ-MM-TT` (FRS-01).
 *
 * Aus den örtlichen Bestandteilen und nicht aus `toISOString()`: Letzteres ist
 * UTC und liefert abends den Vortag — und abends ist genau die Zeit, zu der ein
 * Trainer seinen Tag zurückmeldet.
 */
function heute(): string {
  const d = new Date();
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

/**
 * Die Teilnehmer des eigenen Kurses (TAG-36, TAG-42 i.d.F. ÄA-24).
 *
 * ============================================================================
 * ZUM ANSEHEN UND NICHT ZUM ABHAKEN
 * ============================================================================
 *
 * Auftraggeber, 13.09.2026: „der trainer soll die teilnehmer nicht abhaken,
 * aber ... der trainer soll sich die teilnehmernamen des kurses, egal ob einzel
 * oder firmenkurs, anzeigen lassen können."
 *
 * Deshalb steht hier kein Knopf an der Zeile. Die Anwesenheit erfasst der
 * Empfang; diese Liste beantwortet die eine Frage, die der Trainer am Platz
 * hat: Wen rufe ich auf?
 *
 * ============================================================================
 * SIE WIRD ERST GEHOLT, WENN SIE GEBRAUCHT WIRD (RES-30, RES-31)
 * ============================================================================
 *
 * Zwei Gründe, und der zweite wiegt schwerer: Auf einem Telefon im Mobilfunk
 * ist eine Abfrage, die niemand liest, kein Nebenaspekt — und eine Namensliste,
 * die ungefragt über den Draht geht, ist eine Datenfreigabe, die niemand
 * gebraucht hat (RES-31, Datensparsamkeit).
 *
 * Der Fehlschlag steht IN der Karte und nicht in der Kopfmeldung: „noch nicht
 * freigegeben" und „keine Vereinbarung" gehören zu diesem Termin, und die
 * anderen Einsätze bleiben davon unberührt.
 */
function Teilnehmerliste({ termin }: { readonly termin: string }): JSX.Element {
  const [offen, setzeOffen] = useState(false);
  const [liste, setzeListe] = useState<readonly Gruppenteilnehmer[] | undefined>(undefined);
  const [grund, setzeGrund] = useState<string | undefined>(undefined);

  const holen = useCallback(async (): Promise<void> => {
    try {
      const antwort = await holeTeilnehmer(termin);
      setzeListe(antwort.teilnehmer);
      setzeGrund(undefined);
    } catch (ursache) {
      setzeListe([]);
      setzeGrund(
        ursache instanceof ApiFehler
          ? ursache.meldung.wasGeschah
          : 'Die Teilnehmer konnten nicht geladen werden.',
      );
    }
  }, [termin]);

  if (!offen) {
    return (
      <div className="dlk__teilnehmer">
        <button
          type="button"
          className="knopf"
          onClick={() => {
            setzeOffen(true);
            void holen();
          }}
        >
          Teilnehmer anzeigen
        </button>
      </div>
    );
  }

  return (
    <div className="dlk__teilnehmer">
      {liste === undefined && grund === undefined && (
        <p role="status" className="dlk__warten">
          Einen Augenblick …
        </p>
      )}
      {grund !== undefined && (
        <p className="hinweiszeile" role="status">
          {grund}
        </p>
      )}
      {liste !== undefined && liste.length === 0 && grund === undefined && (
        <p className="hinweiszeile" role="status">
          Zu diesem Termin ist noch niemand gebucht.
        </p>
      )}
      {liste !== undefined && liste.length > 0 && (
        <>
          <p className="dlk__teilnehmerzahl">
            {liste.length === 1 ? '1 Teilnehmer' : `${String(liste.length)} Teilnehmer`}
          </p>
          <ol className="dlk__namen">
            {liste.map((t) => (
              <li key={t.teilnehmer}>
                {/*
                  * DER PLATZ OHNE NAMEN STEHT MIT (BUC-69, TAG-73).
                  *
                  * Ihn wegzulassen wäre die bequeme Liste und die falsche: Der
                  * Trainer zählt am Platz ab, und dann fehlt ihm einer. Wer
                  * dazugehört, klärt der Empfang — deshalb steht es da.
                  */}
                {t.name ?? <span className="dlk__ort">Platz {t.platz}, noch kein Name</span>}
              </li>
            ))}
          </ol>
        </>
      )}
      <button
        type="button"
        className="knopf"
        onClick={() => {
          setzeOffen(false);
        }}
      >
        Zuklappen
      </button>
    </div>
  );
}

/**
 * Die Rückmeldung zu einem Einsatz (RES-77, TAG-43).
 *
 * ============================================================================
 * DREI BEDIENSCHRITTE, WIE RES-30 SIE ZÄHLT
 * ============================================================================
 *
 * Aufklappen, Art wählen, senden. Die Art steht als LISTE VON KNÖPFEN und nicht
 * als Auswahlfeld: Ein `select` auf einem Telefon öffnet ein Rad, das man
 * drehen und bestätigen muss — zwei Schritte für eine Angabe, die fünf
 * Möglichkeiten hat.
 *
 * DIE ZEITEN ERSCHEINEN NUR, WO SIE ETWAS BEDEUTEN. Bei „kürzer" und „länger"
 * sind sie der ganze Gehalt der Meldung (RES-77: die Abweichung wirkt auf die
 * Abrechnungsgrundlage), bei „fiel aus" wären sie ein Widerspruch. Der Server
 * weist beides ab; die Maske fragt es gar nicht erst.
 *
 * DER EIGENE ZUSTAND STEHT IN DIESER KOMPONENTE. Wird sie geschlossen und neu
 * geöffnet, fängt das Formular leer an — das ist die richtige Vorgabe: Eine
 * halb getippte Meldung von gestern wäre schlimmer als keine.
 */
function Rueckmeldeform({
  einsatz,
  laeuft,
  beiMeldung,
}: {
  readonly einsatz: Einsatz;
  readonly laeuft: boolean;
  readonly beiMeldung: (eingabe: {
    readonly art: Rueckmeldungsart;
    readonly vonZeit?: string;
    readonly bisZeit?: string;
    readonly bemerkung?: string;
  }) => void;
}): JSX.Element {
  const [offen, setzeOffen] = useState(false);
  const [art, setzeArt] = useState<Rueckmeldungsart>('wie_beauftragt');
  const [vonZeit, setzeVonZeit] = useState(einsatz.von);
  const [bisZeit, setzeBisZeit] = useState(einsatz.bis);
  const [bemerkung, setzeBemerkung] = useState('');

  const mitZeiten = art === 'verkuerzt' || art === 'verlaengert';

  if (!offen) {
    return (
      <div className="dlk__rueckmeldung">
        {einsatz.rueckmeldung === null ? (
          <button
            type="button"
            className="knopf knopf--haupt"
            onClick={() => {
              setzeOffen(true);
            }}
          >
            Rückmeldung geben
          </button>
        ) : (
          <>
            <p className="dlk__gemeldet">
              <strong>{RUECKMELDUNGSNAME[einsatz.rueckmeldung.art]}</strong>
              {einsatz.rueckmeldung.vonZeit !== null && (
                <> · {einsatz.rueckmeldung.vonZeit}–{einsatz.rueckmeldung.bisZeit} Uhr</>
              )}
            </p>
            {einsatz.rueckmeldung.bemerkung !== null && (
              <p className="dlk__hinweis">{einsatz.rueckmeldung.bemerkung}</p>
            )}
            <button
              type="button"
              className="knopf"
              onClick={() => {
                setzeOffen(true);
              }}
            >
              Ändern
            </button>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="dlk__rueckmeldung">
      <fieldset className="wahlgruppe">
        <legend>Wie lief der Einsatz?</legend>
        {RUECKMELDUNGSARTEN.map((a) => (
          <label key={a} className="wahlreihe">
            <input
              type="radio"
              name={`art-${einsatz.belegung}`}
              value={a}
              checked={art === a}
              onChange={() => {
                setzeArt(a);
              }}
            />
            {RUECKMELDUNGSNAME[a]}
          </label>
        ))}
      </fieldset>

      {mitZeiten && (
        <div className="dlk__zeitfelder">
          <label className="dlk__feld">
            Tatsächlich von{' '}
            <input
              type="time"
              value={vonZeit}
              onChange={(e) => {
                setzeVonZeit(e.target.value);
              }}
            />
          </label>
          <label className="dlk__feld">
            bis{' '}
            <input
              type="time"
              value={bisZeit}
              onChange={(e) => {
                setzeBisZeit(e.target.value);
              }}
            />
          </label>
        </div>
      )}

      <label className="dlk__feld">
        {/*
          * TAG-43: „Vorkommnisse, Auffälligkeiten, Hinweise für Folgetermine."
          * Bei einer Vertretung ist es Pflicht — die Disposition muss wissen,
          * wer gefahren ist, denn daran hängt das Honorar (RES-76).
          */}
        {art === 'vertreten' ? 'Wer ist gefahren?' : 'Vorkommnisse, Hinweise (freiwillig)'}
        <textarea
          rows={2}
          value={bemerkung}
          onChange={(e) => {
            setzeBemerkung(e.target.value);
          }}
        />
      </label>

      <div className="dlk__knoepfe">
        <button
          type="button"
          className="knopf knopf--haupt"
          disabled={laeuft || (art === 'vertreten' && bemerkung.trim() === '')}
          onClick={() => {
            beiMeldung({
              art,
              ...(mitZeiten ? { vonZeit, bisZeit } : {}),
              ...(bemerkung.trim() === '' ? {} : { bemerkung: bemerkung.trim() }),
            });
            setzeOffen(false);
          }}
        >
          Senden
        </button>
        <button
          type="button"
          className="knopf"
          onClick={() => {
            setzeOffen(false);
          }}
        >
          Zurück
        </button>
      </div>
    </div>
  );
}

/**
 * Wie lange die Frist noch läuft (RES-33).
 *
 * „Noch 14 Stunden" ist eine Aufforderung, „12.05.2027, 08:30" ist eine
 * Angabe, die erst gerechnet werden muss. Beides steht da — die Dringlichkeit
 * vorn, der Zeitpunkt als Titel für den, der es genau wissen will.
 */
function alsFrist(iso: string): { readonly kurz: string; readonly genau: string } {
  const ziel = new Date(iso);
  const genau = ziel.toLocaleString('de-DE', {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: 'Europe/Berlin',
  });
  const stunden = Math.floor((ziel.getTime() - Date.now()) / 3_600_000);
  if (stunden < 0) return { kurz: 'Frist abgelaufen', genau };
  if (stunden < 1) return { kurz: 'Frist läuft in unter einer Stunde ab', genau };
  if (stunden < 48) return { kurz: `noch ${String(stunden)} Stunden`, genau };
  return { kurz: `noch ${String(Math.floor(stunden / 24))} Tage`, genau };
}

/**
 * Die Einsätze nach Tagen gebündelt, in der Reihenfolge der Liste (RES-30).
 *
 * DER TAG STEHT EINMAL ÜBER DER GRUPPE UND NICHT IN JEDER KARTE. Ein Trainer
 * hat an einem Tag zwei oder drei Einsätze; wiederholte sich das Datum in jeder
 * Karte, wäre die häufigste Zeile der Liste die uninteressanteste — und auf
 * einem Telefon zählt jede Zeile.
 *
 * Die Reihenfolge kommt aus der Abfrage (`fach.einsaetze_der_ressource` sortiert
 * nach Beginn) und wird hier NICHT neu sortiert: Zwei Sortierungen derselben
 * Liste sind eine zu viel, und die in der Datenbank ist die, die auch der
 * Tagesplan benutzt.
 */
function nachTagen(
  einsaetze: readonly Einsatz[],
): readonly { readonly tag: string; readonly zeilen: readonly Einsatz[] }[] {
  const gruppen: { tag: string; zeilen: Einsatz[] }[] = [];
  for (const e of einsaetze) {
    const letzte = gruppen[gruppen.length - 1];
    if (letzte !== undefined && letzte.tag === e.tag) letzte.zeilen.push(e);
    else gruppen.push({ tag: e.tag, zeilen: [e] });
  }
  return gruppen;
}

/**
 * Die Nachrichten an mich (TAG-34): „Änderungen nach der Freigabe … lösen
 * Benachrichtigungen an die betroffenen Trainer und Dienstleister aus."
 *
 * KLEIN UND FÜR SICH GELADEN. Ohne Nachricht steht hier nichts; scheitert das
 * Laden, auch nicht — der Kalender ist das Wichtigere, und eine zweite
 * Fehlermeldung über ihm wäre Rauschen.
 */
function Trainernachrichten(): JSX.Element | null {
  const [liste, setzeListe] = useState<readonly Trainernachricht[]>([]);
  const laden = useCallback(async (): Promise<void> => {
    try {
      setzeListe((await holeBenachrichtigungen()).nachrichten);
    } catch {
      setzeListe([]);
    }
  }, []);
  useEffect(() => {
    void laden();
  }, [laden]);

  if (liste.length === 0) return null;
  return (
    <section className="dlk__nachrichten" aria-label="Nachrichten an Sie">
      <ul className="benachrichtigungen__liste">
        {liste.map((n) => (
          <li key={n.id} className="benachrichtigung benachrichtigung--neu">
            <p className="benachrichtigung__text">{n.text}</p>
            <p className="benachrichtigung__zeit">
              <time dateTime={n.entstandenAm}>{alsNachrichtenzeit(n.entstandenAm)}</time>
            </p>
            <div className="benachrichtigung__knoepfe">
              <button
                type="button"
                className="knopf knopf--klein"
                onClick={() => {
                  void benachrichtigungGelesen(n.id)
                    .catch(() => undefined)
                    .then(laden);
                }}
              >
                Gelesen
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Ortszeit (FRS-01, LOG-03), unabhängig vom Gerät. */
const NACHRICHTENZEIT = new Intl.DateTimeFormat('de-DE', {
  dateStyle: 'short',
  timeStyle: 'short',
  timeZone: 'Europe/Berlin',
});

function alsNachrichtenzeit(iso: string): string {
  return `${NACHRICHTENZEIT.format(new Date(iso))} Uhr`;
}

export function Trainerkalender({
  beiAbmelden,
  titelVerweis,
}: {
  readonly beiAbmelden: () => void;
  readonly titelVerweis?: React.Ref<HTMLHeadingElement>;
}): JSX.Element {
  const [wer, setzeWer] = useState<Eigene | undefined>(undefined);
  const [liste, setzeListe] = useState<readonly Einsatz[] | undefined>(undefined);
  /*
   * DER FEHLER BLEIBT DER FEHLER UND WIRD NICHT ZU EINEM SATZ VERKUERZT.
   *
   * `Meldung` zeigt alle drei Teile — was geschah, welche Auswirkung, welcher
   * naechste Schritt (ERR-01). Wer hier zu einer Zeichenkette zusammenzieht,
   * wirft den dritten Teil weg, und genau der hilft dem Dienstleister weiter.
   */
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  /** Ein Fehlschlag, der keine Fachmeldung trug — etwa ein Netzabbruch. */
  const [stoerung, setzeStoerung] = useState<string | undefined>(undefined);
  const [erfolg, setzeErfolg] = useState<string | undefined>(undefined);
  /**
   * Der Einsatz, dessen Absage im Pop-up nachgefragt wird (CRM-104).
   *
   * Der ganze Einsatz und nicht nur die Belegung: Schlägt die Absage fehl,
   * wird die Liste neu geholt, und die Karte kann dabei verschwinden (RES-49).
   * Das Pop-up soll trotzdem sagen können, worum es ging.
   */
  const [absage, setzeAbsage] = useState<Einsatz | undefined>(undefined);
  const [grund, setzeGrund] = useState('');
  /** Welcher Knopf gerade läuft — gegen den zweiten Druck (ERR-13). */
  const [laeuft, setzeLaeuft] = useState<string | undefined>(undefined);

  /**
   * Welche der beiden Ansichten offen ist (ÄA-20).
   *
   * „Meine Einsätze" ist die VORGABE, und das ist eine Entscheidung: Wer den
   * Bereich aufruft, will in der Regel wissen, was er selbst zu tun hat — und
   * eine offene Anfrage wartet auf ihn. Der ganze Tag ist das Nachschauen.
   */
  const [ansicht, setzeAnsicht] = useState<'meine' | 'tag'>('meine');
  const [bild, setzeBild] = useState<Tagesbild | null | undefined>(undefined);

  /**
   * Die Einsätze holen.
   *
   * `behalteFehler` IST KEIN SCHALTER FÜR BEQUEMLICHKEIT, SONDERN EINE
   * BERICHTIGUNG. Vorher räumte jedes erfolgreiche Laden die Fehlermeldung weg
   * — auch das Laden, das einem Fehlschlag unmittelbar FOLGT. Die Wirkung: Eine
   * abgelaufene Frist wurde richtig abgewiesen, die Liste wurde richtig neu
   * geholt, und der Dienstleister sah nichts außer einer Zeile, die plötzlich
   * anders aussah. Der Prüffall zu ERR-01 hat es gefunden.
   */
  const laden = useCallback(async (behalteFehler = false): Promise<void> => {
    try {
      const [meins, einsaetze] = await Promise.all([holeIch(), holeEinsaetze()]);
      setzeWer(meins);
      setzeListe(einsaetze.einsaetze);
      if (!behalteFehler) {
        setzeFehler(undefined);
        setzeStoerung(undefined);
      }
    } catch (ursache) {
      if (ursache instanceof ApiFehler) setzeFehler(ursache);
      else setzeStoerung('Die Einsätze konnten nicht geladen werden.');
    }
  }, []);

  useEffect(() => {
    void laden();
  }, [laden]);

  /**
   * Das Tagesbild wird ERST GEHOLT, WENN ES GEBRAUCHT WIRD (RES-30).
   *
   * Es ist die größere Antwort — der ganze Tag eines Standorts —, und die
   * meisten Aufrufe enden bei den eigenen Einsätzen. Auf einem Telefon im Netz
   * des Mobilfunks ist eine Abfrage, die niemand liest, kein Nebenaspekt.
   *
   * Der Fehler landet in derselben Meldung wie alles andere: Ein Externer ohne
   * Auftragsverarbeitungsvereinbarung bekommt hier eine Abweisung nach RES-39,
   * und die erklärt sich selbst.
   */
  const ladeTag = useCallback(async (): Promise<void> => {
    try {
      const antwort = await holeTagesbild();
      setzeBild(antwort.tagesbild);
    } catch (ursache) {
      setzeBild(null);
      if (ursache instanceof ApiFehler) setzeFehler(ursache);
      else setzeStoerung('Der Tagesplan konnte nicht geladen werden.');
    }
  }, []);

  useEffect(() => {
    if (ansicht === 'tag' && bild === undefined) void ladeTag();
  }, [ansicht, bild, ladeTag]);

  /**
   * Zusagen oder absagen und die Liste neu holen (RES-33).
   *
   * NEU GEHOLT UND NICHT ÖRTLICH GEÄNDERT. Eine Zusage kann eine zweite
   * Anfrage verfallen lassen (RES-49), und eine Absage nimmt die Zeile ganz
   * weg. Wer die Liste selbst fortschreibt, zeigt danach einen Zustand, den es
   * auf dem Server nicht gibt.
   */
  /**
   * Zurückmelden, was aus dem Einsatz geworden ist (RES-77, TAG-43).
   *
   * NEU GEHOLT WIE BEI DER ANTWORT: Was der Server daraus macht — und was er
   * abweist —, steht danach in der Liste. Eine Maske, die ihre eigene Eingabe
   * anzeigt, zeigt eine Meldung, die es auf dem Server nicht gibt.
   */
  const melden = useCallback(
    async (
      belegung: string,
      eingabe: {
        readonly art: Rueckmeldungsart;
        readonly vonZeit?: string;
        readonly bisZeit?: string;
        readonly bemerkung?: string;
      },
    ): Promise<void> => {
      setzeLaeuft(belegung);
      setzeFehler(undefined);
      setzeStoerung(undefined);
      setzeErfolg(undefined);
      try {
        await rueckmelden(belegung, eingabe);
        setzeErfolg(
          eingabe.art === 'wie_beauftragt'
            ? 'Danke — der Einsatz ist als durchgeführt vermerkt.'
            : 'Danke. Die Disposition sieht die Abweichung; sie wirkt auf die Abrechnung.',
        );
        await laden();
      } catch (ursache) {
        if (ursache instanceof ApiFehler) setzeFehler(ursache);
        else setzeStoerung('Die Rückmeldung konnte nicht gespeichert werden.');
      } finally {
        setzeLaeuft(undefined);
      }
    },
    [laden],
  );

  const zusagen = useCallback(
    async (belegung: string): Promise<void> => {
      setzeLaeuft(belegung);
      setzeFehler(undefined);
      setzeStoerung(undefined);
      setzeErfolg(undefined);
      try {
        await antworten(belegung, true, undefined);
        /*
         * DIE ZUSAGE NENNT DEN WEG ZURUECK, WEIL SIE KEINEN HAT.
         *
         * Ein Druck genuegt fuer die Zusage — RES-30 zaehlt sie unter den drei
         * Handgriffen, und eine Rueckfrage waere ein Schritt zu viel. Auch die
         * Rueckfrage im Pop-up (CRM-104) bleibt ihr deshalb erspart. Nur: Die
         * zweite Antwort auf dieselbe Anfrage weist der Server ab (RES-33).
         * Wer sich vertippt, kann es also nicht selbst richten und muss es
         * wissen.
         */
        setzeErfolg(
          'Zugesagt. Der Einsatz steht jetzt in Ihrer Liste. Haben Sie sich '
            + 'vertippt? Die Zusage lässt sich hier nicht zurücknehmen — melden '
            + 'Sie sich bitte bei der Disposition.',
        );
        await laden();
      } catch (ursache) {
        if (ursache instanceof ApiFehler) setzeFehler(ursache);
        else setzeStoerung('Die Antwort konnte nicht gespeichert werden.');
        /*
         * Bei einer abgelaufenen Frist stimmt die Liste nicht mehr (RES-49) —
         * die Meldung bleibt trotzdem stehen, denn sie ist die Erklärung
         * dafür, dass die Zeile gleich anders aussieht.
         */
        await laden(true);
      } finally {
        setzeLaeuft(undefined);
      }
    },
    [laden],
  );

  /**
   * Die Absage aus der Rückfrage (CRM-104, RES-33) — sie WIRFT, statt zu fangen.
   *
   * Der Fehler gehört ins Pop-up, das dabei offen bleibt; auf der Seite stünde
   * er hinter dem Fenster ein zweites Mal. Neu geholt wird die Liste trotzdem:
   * Eine abgelaufene Frist hat die Anfrage schon weitergegeben (RES-49), und
   * die Karte soll das zeigen, sobald das Pop-up zu ist.
   */
  const absagen = useCallback(
    async (einsatz: Einsatz): Promise<void> => {
      setzeLaeuft(einsatz.belegung);
      setzeFehler(undefined);
      setzeStoerung(undefined);
      setzeErfolg(undefined);
      try {
        await antworten(einsatz.belegung, false, grund);
      } catch (ursache) {
        await laden(true);
        throw ursache instanceof ApiFehler
          ? ursache
          : new Error('Die Absage konnte nicht gespeichert werden.');
      } finally {
        setzeLaeuft(undefined);
      }
      setzeAbsage(undefined);
      setzeGrund('');
      setzeErfolg('Abgesagt. Die Disposition sucht Ersatz.');
      await laden();
    },
    [grund, laden],
  );

  const offene = (liste ?? []).filter((e) => e.angefragt);
  const feste = (liste ?? []).filter((e) => !e.angefragt);

  return (
    <main className="dlk">
      <header className="dlk__kopf">
        <h1 className="dlk__titel" tabIndex={-1} ref={titelVerweis}>
          Meine Einsätze
        </h1>
        {wer !== undefined && (
          <p className="dlk__wer">
            {wer.bezeichnung}
            {wer.standort !== null && ` · ${wer.standort}`}
          </p>
        )}
        {/*
          * DER EINZIGE WEG HINAUS IST DAS ABMELDEN.
          *
          * Keine Navigation, kein Verweis in das CRM, keine Suche (RES-30).
          */}
        <button type="button" className="knopf knopf--klein dlk__abmelden" onClick={beiAbmelden}>
          <Sinnbild name="sign-out" gewicht="light" />
          Abmelden
        </button>
      </header>

      {/*
        * AKTUALISIEREN ALS KNOPF, NICHT ALS TASTE.
        *
        * Auf einem Telefon gibt es kein F5, und der Browserknopf steckt hinter
        * einem Menü. Gebraucht wird er an zwei Stellen: Eine Frist ist
        * abgelaufen, während die Seite offen stand (RES-49 — dann ist die
        * Anfrage weitergegeben), oder die Disposition hat einen Einsatz
        * nachgetragen.
        */}
      <div className="dlk__werkzeuge">
        <button
          type="button"
          className="knopf knopf--klein"
          disabled={liste === undefined}
          onClick={() => void laden()}
        >
          <Sinnbild name="arrows-clockwise" gewicht="light" />
          Aktualisieren
        </button>
      </div>

      <Trainernachrichten />

      {fehler !== undefined && <Meldung fehler={fehler} />}
      {stoerung !== undefined && (
        <p className="hinweiszeile" role="alert">
          {stoerung}
        </p>
      )}
      {erfolg !== undefined && (
        <p className="hinweiszeile" role="status">
          {erfolg}
        </p>
      )}

      {/*
        * ZWEI ANSICHTEN, ZWEI KNÖPFE (ÄA-20).
        *
        * Als Knöpfe mit `aria-pressed` und nicht als Reiterleiste: Zwei Schalter
        * sind auf 360 Pixeln zwei volle Trefferflächen, eine Reiterleiste wäre
        * eine Zeile mit zwei schmalen Zielen. Und ein Vorleseprogramm sagt bei
        * `aria-pressed` von selbst, welcher gedrückt ist.
        */}
      <div className="dlk__ansichten" role="group" aria-label="Ansicht">
        <button
          type="button"
          className={`knopf dlk__ansicht${ansicht === 'meine' ? ' dlk__ansicht--offen' : ''}`}
          aria-pressed={ansicht === 'meine'}
          onClick={() => setzeAnsicht('meine')}
        >
          Meine Einsätze
        </button>
        <button
          type="button"
          className={`knopf dlk__ansicht${ansicht === 'tag' ? ' dlk__ansicht--offen' : ''}`}
          aria-pressed={ansicht === 'tag'}
          onClick={() => setzeAnsicht('tag')}
        >
          Der ganze Tag
        </button>
      </div>

      {ansicht === 'meine' &&
        liste === undefined &&
        fehler === undefined &&
        stoerung === undefined && (
          <p role="status" className="dlk__warten">
            Einen Augenblick …
          </p>
        )}

      {/*
        * DIE OFFENEN ANFRAGEN STEHEN OBEN.
        *
        * Sie sind das, wofür der Kalender aufgerufen wird: Eine Anfrage mit
        * Frist wartet auf eine Antwort, und alles andere ist Nachschauen.
        */}
      {ansicht === 'meine' && offene.length > 0 && (
        <section className="dlk__block" aria-labelledby="dlk-offen">
          <h2 id="dlk-offen" className="dlk__blocktitel">
            Offene Anfragen ({offene.length})
          </h2>
          <ul className="dlk__karten">
            {offene.map((e) => {
              const frist = e.frist === null ? undefined : alsFrist(e.frist);
              return (
                <li key={e.belegung} className="dlk__karte dlk__karte--offen">
                  {/* Bei den offenen Anfragen steht der Tag IN der Karte: Es sind
                      wenige, sie gehören nicht zusammen, und jede ist eine
                      eigene Entscheidung. */}
                  <p className="dlk__tag">{alsTag(e.tag)}</p>
                  <p className="dlk__zeit">
                    <Sinnbild name="clock" />
                    {e.von} – {e.bis} Uhr
                    <span className="dlk__dauer">
                      {' '}
                      ({e.dauerStunden.toLocaleString('de-DE')} h)
                    </span>
                  </p>
                  <p className="dlk__leistung">{e.leistung}</p>
                  <p className="dlk__ort">
                    <Sinnbild name="map-pin" />
                    {e.ort}
                  </p>
                  {frist !== undefined && (
                    <p className="dlk__frist" title={frist.genau}>
                      Antwort erbeten: {frist.kurz}
                    </p>
                  )}

                  <div className="dlk__knoepfe">
                    {/* RES-30: ein Druck, keine Rückfrage. */}
                    <button
                      type="button"
                      className="knopf knopf--haupt"
                      disabled={laeuft === e.belegung}
                      onClick={() => void zusagen(e.belegung)}
                    >
                      Zusagen
                    </button>
                    <button
                      type="button"
                      className="knopf knopf--eingriff"
                      disabled={laeuft === e.belegung}
                      onClick={() => {
                        setzeAbsage(e);
                        setzeGrund('');
                      }}
                    >
                      Absagen …
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {/*
        * „EINGEPLANT" UND NICHT „ZUGESAGT".
        *
        * Hier steht alles, was ihn bindet und keine Antwort mehr erwartet —
        * und das ist mehr als das Zugesagte: Eine vorläufige Zuordnung ohne
        * Frist ist ein Halten der Disposition (RES-22). Sie bindet ihn, aber
        * gefragt wurde er nicht. Die Überschrift „Zugesagte Einsätze" hätte
        * behauptet, er habe ja gesagt; der Stand an der Zeile sagt, was
        * wirklich gilt.
        */}
      {ansicht === 'meine' && (
      <section className="dlk__block" aria-labelledby="dlk-feste">
        <h2 id="dlk-feste" className="dlk__blocktitel">
          Eingeplante Einsätze
        </h2>
        {liste !== undefined && feste.length === 0 && (
          <p className="hinweiszeile" role="status">
            Für die nächsten Wochen steht nichts an.
          </p>
        )}
        {nachTagen(feste).map((gruppe) => (
          <div key={gruppe.tag} className="dlk__tagesgruppe">
            <h3 className="dlk__tag">{alsTag(gruppe.tag)}</h3>
            <ul className="dlk__karten">
              {gruppe.zeilen.map((e) => (
                <li key={e.belegung} className="dlk__karte">
                  <p className="dlk__zeit">
                    <Sinnbild name="clock" />
                    {e.von} – {e.bis} Uhr
                    <span className="dlk__dauer">
                      {' '}
                      ({e.dauerStunden.toLocaleString('de-DE')} h)
                    </span>
                  </p>
                  <p className="dlk__leistung">{e.leistung}</p>
                  <p className="dlk__ort">
                    <Sinnbild name="map-pin" />
                    {e.ort}
                  </p>
                  <p className="dlk__stand">{STANDNAME[e.stand] ?? e.stand}</p>
                  {/*
                    * RES-31: Die Durchführungsangaben werden erst kurz vorher
                    * frei. Solange sie es nicht sind, steht der Satz da — sonst
                    * hielte der Dienstleister die leere Karte für einen Fehler.
                    */}
                  {!e.detailsFrei && (
                    <p className="dlk__hinweis">
                      Die Angaben zur Durchführung werden kurz vor dem Termin freigegeben.
                    </p>
                  )}
                  {/*
                    * DIE TEILNEHMER DES EIGENEN KURSES (TAG-36, TAG-42, ÄA-24).
                    *
                    * Erst im Freigabefenster: Vorher weist der Server sie ab
                    * (RES-31), und ein Knopf, der in eine Abweisung führt, ist
                    * ein Versprechen, das die Maske nicht halten kann.
                    */}
                  {e.detailsFrei && <Teilnehmerliste termin={e.termin} />}
                  {/*
                    * DIE RÜCKMELDUNG (RES-77, TAG-43).
                    *
                    * ERST AB DEM TAG DES EINSATZES. Was morgen ist, ist noch
                    * nichts geworden — ein Feld dafür wäre die Einladung, den
                    * Ausfall vorher zu melden, und das ist eine Absage und
                    * gehört an die Disposition (RES-33).
                    *
                    * SIE STEHT AN DER KARTE UND NICHT IN EINER UNTERSEITE.
                    * RES-30 zählt die Bedienschritte: Einstieg, Knopf, fertig.
                    */}
                  {e.tag <= heute() && (
                    <Rueckmeldeform
                      einsatz={e}
                      laeuft={laeuft === e.belegung}
                      beiMeldung={(eingabe) => void melden(e.belegung, eingabe)}
                    />
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </section>
      )}

      {/*
        * DER GANZE TAG (ÄA-20).
        *
        * Der Auftraggeber am 11.09.2026: „damit er weiß, was um ihn herum
        * passiert". Deshalb steht hier JEDER Termin des Standorts und nicht nur
        * der eigene — die eigenen sind markiert.
        *
        * Nach Zeit geordnet, denn so liest man einen Tag. Die Karte nennt das
        * Produkt, die Zeit, wer es führt, für wen es ist und die Zahl der
        * Teilnehmer; darunter der Ablauf mit Ort. Keine Preise, keine
        * Teilnehmernamen (RES-31 bleibt darin unberührt).
        */}
      {ansicht === 'tag' && (
        <section className="dlk__block" aria-labelledby="dlk-tag">
          <h2 id="dlk-tag" className="dlk__blocktitel">
            {bild === undefined || bild === null
              ? 'Der ganze Tag'
              : `${alsTag(bild.tag)} · ${bild.standortname}`}
          </h2>

          {bild === undefined && fehler === undefined && stoerung === undefined && (
            <p role="status" className="dlk__warten">
              Einen Augenblick …
            </p>
          )}

          {bild === null && fehler === undefined && stoerung === undefined && (
            <p className="hinweiszeile" role="status">
              Für heute ist an Ihrem Standort kein Betriebstag angelegt.
            </p>
          )}

          {bild !== undefined && bild !== null && (
            <>
              {/*
                * TAG-33: Ein Entwurf ändert sich noch. Wer danach fährt, muss
                * wissen, ob der Tag freigegeben ist — sonst richtet er sich nach
                * einem Plan, den die Disposition gerade umstellt.
                */}
              {!bild.freigegeben && (
                <p className="hinweiszeile" role="status">
                  Dieser Tag ist noch nicht freigegeben und kann sich ändern.
                </p>
              )}

              {bild.termine.length === 0 && (
                <p className="hinweiszeile" role="status">
                  An diesem Tag ist kein Training eingeplant.
                </p>
              )}

              <ul className="dlk__karten">
                {bild.termine.map((t) => (
                  <li
                    key={t.termin}
                    className={`dlk__karte${t.meiner ? ' dlk__karte--meiner' : ''}`}
                  >
                    <p className="dlk__zeit">
                      <Sinnbild name="clock" />
                      {t.beginn} – {t.ende} Uhr
                      {t.meiner && <span className="dlk__marke"> mein Einsatz</span>}
                    </p>
                    <p className="dlk__leistung">{t.produkt}</p>
                    <p className="dlk__ort">
                      {/*
                        * EIN DING, EIN NAME (BUC-66).
                        *
                        * Hier stand „Firmentermin" und „Einzelbuchungen" — die
                        * Tagesplanung nennt dieselben zwei Dinge
                        * „Exklusivtermin" und „Einzelplatzverkauf", und das ist
                        * der Wortlaut von BUC-66. Zwei Namen für eine Sache
                        * sind einer zu viel, sobald jemand am Telefon über
                        * beide Masken spricht.
                        */}
                      {t.terminart === 'exklusiv'
                        ? (t.kundenname ?? 'Exklusivtermin')
                        : 'Einzelplatzverkauf'}
                      {' · '}
                      {t.teilnehmer === 1 ? '1 Teilnehmer' : `${String(t.teilnehmer)} Teilnehmer`}
                    </p>
                    {t.trainer.length > 0 && <p className="dlk__ort">{t.trainer.join(', ')}</p>}

                    {t.ablauf.length > 0 && (
                      <ul className="dlk__ablauf">
                        {t.ablauf.map((a, stelle) => (
                          <li key={`${a.von}-${String(stelle)}`}>
                            <span className="dlk__ablaufzeit">{a.von}</span> {a.was}
                            {a.ort !== null && <span className="dlk__ort"> · {a.ort}</span>}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      )}

      {/*
        * WAS DIESE MASKE NICHT KANN, STEHT DRIN.
        *
        * Verfügbarkeiten und Abwesenheiten gehören nach RES-29 und RES-34
        * hierher und sind noch nicht gebaut. Ein Bereich, der es verschweigt,
        * lässt den Dienstleister suchen; ein Satz sagt ihm, wohin er sich
        * wendet.
        */}
      {/*
        * DIE ABSAGE FRAGT IM POP-UP NACH (CRM-104, RES-33).
        *
        * Der Grund bleibt freiwillig und steht darum NICHT im `grund` der
        * Rückfrage — der kennt nur Pflichtgründe —, sondern als eigenes Feld
        * darin. `dlk__absage` gibt ihm die 16 Pixel, mit denen iOS beim Tippen
        * nicht heranzoomt (RES-30).
        */}
      {absage !== undefined && (
        <Rueckfrage
          frage="Einsatz absagen?"
          unterzeile={`${alsTag(absage.tag)} · ${absage.von} – ${absage.bis} Uhr`}
          tat="Absage senden"
          beiBestaetigen={() => absagen(absage)}
          beiSchliessen={() => {
            setzeAbsage(undefined);
            setzeGrund('');
          }}
        >
          <p>
            {absage.leistung} in {absage.ort}. Die Disposition fragt danach den Nächsten an;
            die Absage lässt sich hier nicht zurücknehmen.
          </p>
          <div className="dlk__absage">
            <label className="feld__beschriftung" htmlFor={`grund-${absage.belegung}`}>
              Grund (freiwillig)
            </label>
            <input
              id={`grund-${absage.belegung}`}
              className="feld__eingabe"
              type="text"
              maxLength={500}
              value={grund}
              onChange={(ev) => setzeGrund(ev.target.value)}
            />
          </div>
        </Rueckfrage>
      )}

      <footer className="dlk__fuss">
        <p>
          Abwesenheiten und Verfügbarkeiten melden Sie bitte noch der Disposition. Der
          Eintrag in diesem Bereich folgt.
        </p>
      </footer>
    </main>
  );
}
